// Merges static meshes, toon shading and the see-through occluders.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { scene, sky } from './scene.js';
import { ducks } from '../world/eixample.js';
import { cranes } from '../world/sagrada.js';
import { walkers } from '../people/models.js';
import { sups } from '../people/crowd.js';
import { lean, moto, riderG, wheels } from '../vehicles/scooter.js';
import { cars } from '../vehicles/cars.js';
import { boats, gulls } from '../world/sea-life.js';
import { nameGroup } from '../game/intro.js';
import { STOPS } from '../data/stops.js';
import { arrow } from '../game/stop-markers.js';

export let fadeU, toonify, dynamicRoots;

export function optimiseScene() {
  toonify = function toonify(m) {
    if (!m || !m.isMeshStandardMaterial || m.userData.toon) return;
    m.userData.toon = true;
    const prev = m.onBeforeCompile,
      id = toonIds.get(prev) ?? toonIds.set(prev, toonIds.size).get(prev);
    m.onBeforeCompile = (sh, r) => {
      prev.call(m, sh, r);
      Object.assign(sh.uniforms, fadeU);
      sh.fragmentShader =
        FADE_GLSL +
        sh.fragmentShader.replace(
          '#include <clipping_planes_fragment>',
          '#include <clipping_planes_fragment>\n  seeThrough(-vViewPosition);',
        );
      sh.fragmentShader = sh.fragmentShader.replace(
        '#include <opaque_fragment>',
        `{
      vec3 alb = max(material.diffuseColor, vec3(0.0));
      vec3 lit = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
      float x = dot(lit, vec3(.3, .59, .11)) / max(dot(alb, vec3(.3, .59, .11)), .03);
      float band = mix(.45 + .5 * clamp(x, 0.0, 1.1), .5 + .33 * smoothstep(.5, .64, x) + .09 * smoothstep(1.05, 1.35, x), .65);
      vec3 tint = mix(vec3(.8, .88, 1.06), vec3(1.05, 1.0, .93), smoothstep(.45, .7, x));
      float rim = pow(1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0), 3.0);
      outgoingLight = alb * band * tint + (reflectedLight.directSpecular + reflectedLight.indirectSpecular) * .5 + totalEmissiveRadiance + rim * .16 * vec3(1.0, .97, .9) * smoothstep(.45, .7, x);
    }
    #include <opaque_fragment>`,
      );
    };
    m.customProgramCacheKey = () => 'toon' + id;
  };
  // ---------- optimisation ----------
  // merge every static mesh that shares a material into a single draw call (in the root's local space)
  function freeze(root, isDynamic) {
    root.updateMatrixWorld(true);
    const inv = new THREE.Matrix4().copy(root.matrixWorld).invert(),
      groups = new Map(),
      victims = [],
      m = new THREE.Matrix4();
    (function visit(o) {
      if (o !== root && isDynamic(o)) return;
      if (
        o.isMesh &&
        !o.isInstancedMesh &&
        !Array.isArray(o.material) &&
        o.visible &&
        !o.children.length &&
        o.geometry.attributes.normal
      ) {
        const mat = o.material,
          key = mat.uuid + (o.castShadow ? 'c' : '') + (o.receiveShadow ? 'r' : '');
        let src = o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone();
        src.applyMatrix4(m.multiplyMatrices(inv, o.matrixWorld));
        const g = new THREE.BufferGeometry(),
          n = src.attributes.position.count;
        g.setAttribute('position', src.attributes.position);
        g.setAttribute('normal', src.attributes.normal);
        g.setAttribute(
          'uv',
          src.attributes.uv || new THREE.BufferAttribute(new Float32Array(n * 2), 2),
        );
        if (mat.vertexColors)
          g.setAttribute(
            'color',
            src.attributes.color || new THREE.BufferAttribute(new Float32Array(n * 3).fill(1), 3),
          );
        if (!groups.has(key))
          groups.set(key, { mat, cast: o.castShadow, recv: o.receiveShadow, geos: [] });
        groups.get(key).geos.push(g);
        victims.push(o);
      }
      for (const c of [...o.children]) visit(c);
    })(root);
    for (const v of victims) v.parent.remove(v);
    for (const { mat, cast, recv, geos } of groups.values()) {
      const merged = new THREE.Mesh(mergeGeometries(geos), mat);
      merged.castShadow = cast;
      merged.receiveShadow = recv;
      root.add(merged);
    }
    return victims.length;
  }
  const toonIds = new Map();
  // See-through occluders while riding: anything standing in a cone between the camera and the scooter is drawn with a
  // screen-door dither (every other pixel or so discarded), which the paint filter smooths into a soft transparency.
  // Works on the merged city meshes because it is decided per pixel, in view space (camera at the origin).
  fadeU = { uFadeTo: { value: new THREE.Vector3() }, uFadeOn: { value: 0 } };
  const FADE_GLSL = `
  uniform vec3 uFadeTo; uniform float uFadeOn;
  void seeThrough(vec3 p) {
    if (uFadeOn < .01) return;
    float L = length(uFadeTo); vec3 d = uFadeTo / L; float t = dot(p, d) / L;
    if (t < .04 || t > .86) return;                       // not the scooter itself, nor what's behind it
    // a wide, gentle falloff so no circle outline shows; 16 dither levels
    // from a 4x4 Bayer matrix make the gradient smooth instead of stepping in rings
    float r = mix(.8, 3.2, t), dist = length(p - d * t * L);
    float f = 1.0 - smoothstep(r * .1, r * 1.25, dist);
    float amount = uFadeOn * .5 * f * f * (3.0 - 2.0 * f);
    vec2 q = mod(floor(gl_FragCoord.xy), 4.0);
    vec2 q2 = mod(q, 2.0), q4 = floor(q / 2.0);
    float bayer = (4.0 * mod(q2.x + q2.y * 3.0, 4.0) + mod(q4.x + q4.y * 3.0, 4.0)) / 16.0 + 1.0 / 32.0;
    if (bayer < amount) discard;
  }
`;
  scene.traverse((o) => {
    if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach(toonify);
  });
  for (const c of cars) freeze(c.g, () => false);
  freeze(lean, (o) => wheels.includes(o) || o === riderG || o.userData.rider);
  dynamicRoots = new Set([
    moto,
    arrow,
    window.foam,
    ...sups.map((s) => s.g),
    ...cars.map((c) => c.g),
    ...walkers.map((w) => w.g),
    ...walkers.filter((w) => w.dog).map((w) => w.dog.g),
    ...ducks.map((d) => d.g),
    ...boats.map((b) => b.g),
    ...gulls.map((g) => g.g),
    ...cranes.map((c) => c.top),
    ...STOPS.map((st) => st.g),
    nameGroup,
  ]);
  freeze(scene, (o) => dynamicRoots.has(o) || o === sky);
  // small instanced details don't need to cast shadows
  scene.traverse((o) => {
    if (o.isInstancedMesh) {
      o.geometry.computeBoundingSphere();
      if (o.geometry.boundingSphere.radius < 0.55) o.castShadow = false;
    }
  });
}
